import { useState, useEffect, useCallback, useRef } from "react";

interface UseVideoProgressProps {
	lessonId?: number;
	videoUrl?: string;
	playing: boolean;
	getPlayerCurrentTime: () => number;
}

interface UseVideoProgressReturn {
	startTime: number;
	saveProgress: (currentTime: number) => void;
	clearProgress: () => void;
}

const PROGRESS_SAVE_INTERVAL = 5000; // 5 seconds
const MIN_RESUME_TIME = 30; // Only resume if more than 30 seconds

/**
 * Hook to manage video progress persistence
 * Saves and restores video progress to/from localStorage
 * Also supports periodic auto-save during playback
 */
export const useVideoProgress = ({
	lessonId,
	videoUrl,
	playing,
	getPlayerCurrentTime,
}: UseVideoProgressProps): UseVideoProgressReturn => {
	const [startTime, setStartTime] = useState<number>(0);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	// Load saved progress when lesson changes
	useEffect(() => {
		if (!lessonId) {
			setStartTime(0);
			return;
		}

		const savedProgress = localStorage.getItem(`video_progress_${lessonId}`);
		if (savedProgress) {
			const progressTime = parseFloat(savedProgress);
			if (progressTime > MIN_RESUME_TIME) {
				setStartTime(progressTime);
			} else {
				setStartTime(0);
			}
		} else {
			setStartTime(0);
		}
	}, [lessonId]);

	// Auto-save progress for non-PandaVideo players
	useEffect(() => {
		// Only auto-save for non-panda videos when playing
		if (!playing || !lessonId || !videoUrl || videoUrl.includes("pandavideo")) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}

		intervalRef.current = setInterval(() => {
			const currentTime = getPlayerCurrentTime();
			if (currentTime > 0) {
				localStorage.setItem(`video_progress_${lessonId}`, currentTime.toString());
			}
		}, PROGRESS_SAVE_INTERVAL);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [playing, lessonId, videoUrl, getPlayerCurrentTime]);

	const saveProgress = useCallback(
		(currentTime: number) => {
			if (lessonId && currentTime > 0) {
				localStorage.setItem(`video_progress_${lessonId}`, currentTime.toString());
			}
		},
		[lessonId]
	);

	const clearProgress = useCallback(() => {
		if (lessonId) {
			localStorage.removeItem(`video_progress_${lessonId}`);
		}
		setStartTime(0);
	}, [lessonId]);

	return {
		startTime,
		saveProgress,
		clearProgress,
	};
};

export type { UseVideoProgressReturn };
